/**
 * شاشة موافقة OAuth 2.1 — يصل إليها المستخدم من Supabase حين يحاول عميل خارجيّ
 * (ChatGPT / Claude / Cursor …) الاتّصال بحسابه في Lecmo عبر خادم MCP.
 *
 * المسار الفعليّ: /.lovable/oauth/consent?authorization_id=…
 * (TanStack يهرّب النقطة الحرفيّة بـ [.] — ممنوع تسمية الملفّ بنقطة في أوّله.)
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import * as React from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";
import { oauth } from "@/lib/oauth-consent";

export const Route = createFileRoute("/.lovable/oauth/consent")({
  // متصفّح فقط: عميل Supabase يقرأ الجلسة من localStorage الغائب في SSR.
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      const next = location.pathname + location.searchStr;
      throw redirect({ to: "/auth", search: { redirect: next, mode: "signin" } });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await oauth().getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: ConsentPage,
  errorComponent: ({ error }) => (
    <main className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className="font-serif text-2xl">Authorization request could not be loaded</h1>
        <p className="text-sm text-muted-foreground">
          {String((error as Error)?.message ?? error)}
        </p>
      </div>
    </main>
  ),
  head: () => ({ meta: [{ title: "Connect an app — Lecmo" }] }),
});

const SCOPE_LABELS: Record<string, { en: string; ar: string }> = {
  openid: { en: "Confirm your Lecmo identity", ar: "تأكيد هويّتك في Lecmo" },
  email: { en: "Share your email address", ar: "مشاركة بريدك الإلكترونيّ" },
  profile: { en: "Share your basic profile", ar: "مشاركة ملفّك الأساسيّ" },
};

function ConsentPage() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const { locale, dir } = useLocale();
  const ar = locale === "ar";
  const [busy, setBusy] = React.useState<"approve" | "deny" | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const clientName = details?.client?.name?.trim() || (ar ? "تطبيق خارجيّ" : "an external app");
  const scopes = (details?.scope ?? "").split(/\s+/).filter(Boolean);

  async function decide(approve: boolean) {
    setBusy(approve ? "approve" : "deny");
    setError(null);
    const api = oauth();
    const { data, error: err } = approve
      ? await api.approveAuthorization(authorization_id)
      : await api.denyAuthorization(authorization_id);
    if (err) {
      setBusy(null);
      setError(err.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(null);
      setError(
        ar
          ? "لم يُرجِع خادم الصلاحيّات رابط عودة."
          : "The authorization server did not return a redirect URL.",
      );
      return;
    }
    window.location.href = target;
  }

  return (
    <main dir={dir} className="min-h-[80vh] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md border border-border bg-card p-7 space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-foreground/70" aria-hidden />
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {ar ? "طلب اتّصال" : "Connection request"}
          </span>
        </div>

        <h1 className="font-serif text-2xl leading-snug">
          {ar ? `ربط ${clientName} بحسابك في Lecmo` : `Connect ${clientName} to your Lecmo account`}
        </h1>

        <p className="text-sm text-muted-foreground leading-relaxed">
          {ar
            ? `سيتمكّن ${clientName} من استخدام أدوات Lecmo نيابةً عنك أثناء تسجيل دخولك: تصفّح العطور، وقراءة طلباتك ونقاطك وقائمة أمنياتك وهداياك.`
            : `${clientName} will be able to use Lecmo's tools as you: browse fragrances and read your orders, loyalty points, wishlist and gifts.`}
        </p>

        {scopes.length > 0 && (
          <ul className="space-y-2 text-sm">
            {scopes.map((s: string) => (
              <li key={s} className="flex gap-2">
                <span aria-hidden>·</span>
                <span>
                  {SCOPE_LABELS[s]
                    ? SCOPE_LABELS[s][ar ? "ar" : "en"]
                    : ar
                      ? `صلاحيّة إضافيّة: ${s}`
                      : `Additional permission requested: ${s}`}
                </span>
              </li>
            ))}
          </ul>
        )}

        {details?.redirect_uri && (
          <p className="text-[11px] text-muted-foreground break-all">
            {ar ? "سيُعاد توجيهك إلى: " : "You'll be redirected to: "}
            {details.redirect_uri}
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          {ar
            ? "هذا لا يتجاوز صلاحيّات Lecmo ولا سياسات الحماية؛ كلّ الأدوات للقراءة فقط."
            : "This does not bypass Lecmo's permissions or backend policies. All tools are read-only."}
        </p>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide(true)}
            className={cn(
              "min-h-[46px] w-full bg-foreground text-background text-[11px] uppercase tracking-[0.18em]",
              "flex items-center justify-center gap-2 disabled:opacity-60",
            )}
          >
            {busy === "approve" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {ar ? "أوافق وربط الحساب" : "Approve and connect"}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide(false)}
            className="min-h-[46px] w-full border border-border text-[11px] uppercase tracking-[0.18em] disabled:opacity-60"
          >
            {ar ? "إلغاء الاتّصال" : "Cancel connection"}
          </button>
        </div>
      </div>
    </main>
  );
}
